import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ExperienceAnalysis } from '../../types';

interface ExperienceSectionProps {
  data: ExperienceAnalysis;
}

const ExperienceSection: React.FC<ExperienceSectionProps> = ({ data }) => {
  const { t } = useTranslation();
  const formatLevel = (level: string): string => {
    switch(level) {
      case 'junior': return t('results.experience.level.junior');
      case 'mid': return t('results.experience.level.mid');
      case 'senior': return t('results.experience.level.senior');
      default: return level;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-5 flex items-center">
        <span className="text-2xl mr-3">🗓️</span>
        <h3 className="text-xl font-semibold">{t('results.experience.title')}</h3>
        <span className="ml-auto text-xl font-bold">{data?.score}%</span>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-semibold text-lg mb-4 text-gray-800">{t('results.experience.summaryTitle')}</h4>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">{t('results.experience.requiredLevel')}:</span>
                <span className={`font-medium ${data?.analysis?.levelMatch ? 'text-green-600' : 'text-orange-600'}`}>
                  {formatLevel(data?.requiredLevel)}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-700">{t('results.experience.yourLevel')}:</span>
                <span className="font-medium text-blue-600">{formatLevel(data?.cvLevel)}</span>
              </div>
              
              <div className="border-t border-gray-200 my-2"></div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-700">{t('results.experience.requiredYears')}:</span>
                <span className={`font-medium ${data?.analysis?.yearMatch ? 'text-green-600' : 'text-orange-600'}`}>
                  {data?.requiredYears} {t('results.experience.years')}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-700">{t('results.experience.yourYears')}:</span>
                <span className="font-medium text-blue-600">
                  {data?.cvYears} {t('results.experience.years')}
                </span>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-semibold text-lg mb-4 text-gray-800">{t('results.experience.compatibilityTitle')}</h4>
            
            <div className="space-y-3">
              <div className="flex items-center">
                <span className={`text-2xl mr-2 ${data?.analysis?.levelMatch ? 'text-green-500' : 'text-amber-500'}`}>
                  {data?.analysis?.levelMatch ? '✓' : '⚠️'}
                </span>
                <span>
                  {data?.analysis?.levelMatch 
                    ? t('results.experience.levelMatch')
                    : t('results.experience.levelMismatch')}
                </span>
              </div>
              
              <div className="flex items-center">
                <span className={`text-2xl mr-2 ${data?.analysis?.yearMatch ? 'text-green-500' : 'text-amber-500'}`}>
                  {data?.analysis?.yearMatch ? '✓' : '⚠️'}
                </span>
                <span>
                  {data?.analysis?.yearMatch 
                    ? t('results.experience.yearsMatch')
                    : t('results.experience.yearsMismatch', { missing: data?.requiredYears - data?.cvYears, plural: data?.requiredYears - data?.cvYears > 1 })}
                </span>
              </div>
              
              <div className="flex items-center">
                <span className={`text-2xl mr-2 ${data?.analysis?.relevantCount > 0 ? 'text-green-500' : 'text-amber-500'}`}>
                  {data?.analysis?.relevantCount > 0 ? '✓' : '⚠️'}
                </span>
                <span>
                  {data?.analysis?.relevantCount > 0
                    ? t('results.experience.relevantFound', { count: data?.analysis?.relevantCount })
                    : t('results.experience.relevantNone')}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {data?.relevantExperiences?.length > 0 && (
          <div>
            <h4 className="font-semibold text-lg mb-4 text-gray-800">{t('results.experience.relevantTitle')}</h4>
            
            <div className="space-y-4">
              {data?.relevantExperiences?.map((exp, index) => (
                <div 
                  key={index}
                  className="bg-gray-50 p-5 rounded-lg border-l-4 border-blue-500 transition-all hover:bg-blue-50 hover:translate-x-1"
                >
                  <div className="font-semibold text-gray-800 mb-2">{exp?.summary?.title}</div>
                  <div className="text-gray-600 text-sm mb-3">{exp?.summary?.details}</div>
                  
                  {(exp?.foundKeywords?.length > 0 || exp?.foundSkills?.length > 0) && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {exp?.foundSkills?.map((skill, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                          {skill}
                        </span>
                      ))}
                      {exp?.foundKeywords?.map((kw, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                          {kw}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {data?.relevantExperiences?.length === 0 && (
          <p className="text-gray-500 italic">{t('results.experience.noRelevant')}</p>
        )}
      </div>
    </div>
  );
};

export default ExperienceSection;